import type { FC, MouseEvent } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Button, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material'
import CheckOutlined from '@mui/icons-material/CheckOutlined'
import UnfoldMoreOutlined from '@mui/icons-material/UnfoldMoreOutlined'

export interface SortOption<T extends string> {
  readonly value: T
  readonly label: string
}

interface OrderByControlProps<T extends string> {
  readonly options: readonly SortOption<T>[]
  readonly value: T
  readonly onChange: (value: T) => void
  readonly ariaLabel?: string
}

export function OrderByControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel = 'Sort order',
}: OrderByControlProps<T>): ReturnType<FC> {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isOpen = Boolean(anchorEl)

  const handleButtonClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleOptionSelect = useCallback(
    (selected: T) => {
      onChange(selected)
      setAnchorEl(null)
    },
    [onChange],
  )

  const currentLabel = options.find((opt) => opt.value === value)?.label ?? options[0]?.label ?? ''

  return (
    <>
      <Button
        onClick={handleButtonClick}
        endIcon={<UnfoldMoreOutlined sx={dropdownIconSx} />}
        size="small"
        sx={buttonSx}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        {currentLabel}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={menuSx}
      >
        {options.map((option) => (
          <SortOptionItem<T>
            key={option.value}
            option={option}
            isSelected={value === option.value}
            onSelect={handleOptionSelect}
          />
        ))}
      </Menu>
    </>
  )
}

// ---------------------------------------------------------------------------
// Private: Sort option item
// ---------------------------------------------------------------------------

interface SortOptionItemProps<T extends string> {
  readonly option: SortOption<T>
  readonly isSelected: boolean
  readonly onSelect: (value: T) => void
}

function SortOptionItem<T extends string>({
  option,
  isSelected,
  onSelect,
}: SortOptionItemProps<T>): ReturnType<FC> {
  const handleClick = useCallback(() => {
    onSelect(option.value)
  }, [onSelect, option.value])

  return (
    <MenuItem onClick={handleClick} selected={isSelected} sx={menuItemSx}>
      <ListItemIcon sx={checkIconContainerSx}>
        {isSelected && <CheckOutlined fontSize="small" sx={checkIconSx} />}
      </ListItemIcon>
      <Typography variant="body2">{option.label}</Typography>
    </MenuItem>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const buttonSx: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 400,
  fontSize: '0.875rem',
  color: 'text.secondary',
  px: 1.5,
  py: 0.5,
  borderRadius: 1,
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'text.primary',
  },
}

const dropdownIconSx: SxProps<Theme> = {
  fontSize: '1.1rem',
  color: 'text.disabled',
}

const menuSx: SxProps<Theme> = {
  '& .MuiPaper-root': {
    minWidth: 140,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 4,
    mt: 0.5,
  },
}

const menuItemSx: SxProps<Theme> = {
  mx: 0.5,
  my: 0.25,
  borderRadius: 1,
  color: 'text.secondary',
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'text.primary',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
  },
  '&.Mui-selected': {
    backgroundColor: 'action.selected',
    color: 'text.primary',
    '&:hover': {
      backgroundColor: 'action.selected',
    },
  },
}

const checkIconContainerSx: SxProps<Theme> = {
  minWidth: 28,
}

const checkIconSx: SxProps<Theme> = {
  color: 'primary.main',
}
